import { useTheme } from '../components/providers/ThemeProvider'
import { BRAND, BRAND_ASSETS } from '../config/brand'

/**
 * Branding for the current theme.
 *
 * The WORDS come from `config/brand.ts` and do not depend on anything — see the
 * note there. Until 2026-09-06 this hook returned a different product name for
 * the diary theme, so changing your colours renamed the company; that is gone.
 *
 * The ARTWORK still switches on light/dark, because that is a real rendering
 * concern rather than an identity one.
 */

/**
 * The diary theme's own flavour copy. The name and tagline are the product's
 * (one product, one name); only the mission line and the pillars below are
 * written for the journal surface.
 */
export const DIARY_FLAVOUR = {
  mission:
    `${BRAND.name} keeps your whole life in one warm place — your dreams as ` +
    'chapters, your people as dossiers, your places as postcards — and ' +
    'helps you write the next page, one day at a time.',
  pillars: [
    { emoji: '📖', label: 'Your days become a story' },
    { emoji: '💌', label: 'Your people, remembered' },
    { emoji: '🗺️', label: 'Your places, alive' },
  ],
} as const

export function useBranding() {
  const { isDark } = useTheme()
  const art = isDark ? BRAND_ASSETS.dark : BRAND_ASSETS.light

  return {
    icon: art.mark,
    horizontal: art.lockup,
    wordmark: art.wordmark,
    socialCard: art.socialCard,
    base: isDark ? '/branding/dark' : '/branding/light',
    isDark,
    /** The product name. Same in every theme. */
    name: BRAND.name,
    /** One line under the name. Same in every theme. */
    tagline: BRAND.tagline,
    /** The assistant's name — the voice that speaks in the first person. */
    assistant: BRAND.assistant,
  }
}
